<template>
  <div class="zhihu-qa studio-layout">
    <!-- 配置面板 -->
    <el-card>
      <template #header>
        <div class="card-header">
          <span>知乎问答配置</span>
          <el-tag type="info" size="small">固定平台：知乎 · 独立提示词 · 不用话题池</el-tag>
        </div>
      </template>

      <el-form :model="form" label-width="120px">
        <el-form-item label="知乎问题" required>
          <el-input
            v-model="form.question"
            type="textarea"
            :rows="4"
            placeholder="输入要回答的知乎问题，例如：同人写手如何选择适合自己的发布平台？"
          />
        </el-form-item>

        <el-form-item label="补充说明">
          <el-input
            v-model="form.extraHint"
            type="textarea"
            :rows="2"
            placeholder="可选：拆方向 / 写正文时的额外约束"
          />
        </el-form-item>

        <el-form-item label="拆方向检索">
          <el-switch
            v-model="form.runSearch"
            active-text="联网 WebSearch"
            inactive-text="不检索，仅根据问题拆方向"
          />
          <p class="field-hint">关闭后更快，但不会带入联网素材；写正文时仍可使用本次已检索到的素材（若有）</p>
        </el-form-item>

        <el-form-item v-if="searchMeta.count != null" label="检索素材">
          <div class="search-meta">
            <el-tag type="success" effect="plain">已检索 {{ searchMeta.count }} 条</el-tag>
            <span
              v-for="(q, i) in (searchMeta.queries || []).slice(0, 3)"
              :key="i"
              class="query-chip"
            >{{ q }}</span>
          </div>
          <p class="field-hint">拆方向时若开启 WebSearch，会先检索再设计角度（通常需 1–3 分钟）</p>
        </el-form-item>

        <el-form-item label=" ">
          <el-button
            type="primary"
            :loading="loadingDirections"
            :disabled="!form.question.trim()"
            @click="fetchDirections"
          >
            {{ form.runSearch ? '生成回答方向（含 WebSearch）' : '生成回答方向（不检索）' }}
          </el-button>
          <el-button :icon="Plus" @click="addCustomDirection">添加自定义方向</el-button>
          <span class="hint-text">也可不点生成，直接手写方向后勾选</span>
        </el-form-item>

        <el-form-item label="写正文模板">
          <el-select
            v-model="form.articlePromptKey"
            placeholder="选择写正文 System 提示词"
            style="min-width: 240px"
            :loading="loadingArticlePrompts"
          >
            <el-option
              v-for="opt in articlePromptOptions"
              :key="opt.key || '__default__'"
              :label="opt.name"
              :value="opt.key"
            >
              <span style="float: left">{{ opt.name }}</span>
              <span style="float: right; color: #909399; font-size: 12px; margin-left: 8px">
                {{ opt.is_default ? '默认' : '自定义' }}
              </span>
            </el-option>
          </el-select>
          <el-button link type="primary" :icon="Refresh" @click="loadArticlePrompts">刷新</el-button>
          <span class="hint-text">在「提示词模板 · 知乎问答」处可新增/编辑变体</span>
        </el-form-item>

        <el-form-item label=" ">
          <el-button
            type="primary"
            size="large"
            :loading="generating"
            :disabled="selectedCount === 0 || !form.question.trim()"
            @click="startGenerate"
          >
            生成知乎正文（{{ selectedCount }} 篇）
          </el-button>
          <el-button size="large" @click="resetForm">重置</el-button>
          <span v-if="selectedCount > 0" class="count-hint">将生成 {{ selectedCount }} 篇知乎回答</span>
        </el-form-item>
      </el-form>

      <el-progress
        v-if="genJob.status === 'running' || genJob.status === 'pending'"
        :percentage="genProgress"
        :stroke-width="10"
        style="margin-top: 4px"
      />
    </el-card>

    <!-- 回答方向列表（表格） -->
    <el-card class="directions-card">
      <template #header>
        <div class="card-header">
          <div class="preview-header-text">
            <span>回答方向</span>
            <p class="preview-note">
              勾选要生成的方向；可在表格中直接编辑（不截断）。共 {{ directions.length }} 条，已选 {{ selectedCount }} 条。正文将并发生成。
            </p>
          </div>
          <div class="header-actions">
            <el-button size="small" link type="primary" :disabled="!directions.length" @click="selectAll">
              全选
            </el-button>
            <el-button size="small" link :disabled="!directions.length" @click="selectNone">
              清空
            </el-button>
          </div>
        </div>
      </template>

      <el-table
        v-if="directions.length"
        ref="dirTableRef"
        :data="directions"
        row-key="_key"
        size="small"
        stripe
        border
      >
        <el-table-column width="52" align="center">
          <template #header>
            <el-checkbox
              :model-value="allDirectionsSelected"
              :indeterminate="directionsIndeterminate"
              @change="toggleSelectAllDirections"
            />
          </template>
          <template #default="{ row }">
            <el-checkbox v-model="row.selected" />
          </template>
        </el-table-column>
        <el-table-column label="#" type="index" width="50" />
        <el-table-column label="方向名称" width="180">
          <template #default="{ row }">
            <el-input v-model="row.label" size="small" placeholder="如：新手痛点" />
          </template>
        </el-table-column>
        <el-table-column label="回答核心方向" min-width="360">
          <template #default="{ row }">
            <el-input
              v-model="row.text"
              type="textarea"
              :rows="3"
              size="small"
              placeholder="核心论点 / 立场 / 叙事主线（完整保留，不截断）"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="72" fixed="right">
          <template #default="{ $index }">
            <el-button link type="danger" size="small" @click="removeDirection($index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-empty v-else description="填写问题后点击「生成回答方向」，或「添加自定义方向」" />
    </el-card>

    <!-- 任务预览（与生成任务页一致） -->
    <el-card v-if="previewJobs.length > 0" class="preview-card">
      <template #header>
        <div class="card-header">
          <div class="preview-header-text">
            <span>任务预览（前 {{ Math.min(previewJobs.length, 10) }} 条）</span>
            <p class="preview-note">
              每条选中方向将生成一篇<strong>知乎</strong>回答，并写入<strong>任务队列</strong>与<strong>文章管理</strong>（ZH-QA）。
            </p>
          </div>
          <el-tag>共 {{ previewJobs.length }} 篇</el-tag>
        </div>
      </template>
      <el-table :data="previewJobs.slice(0, 10)" size="small" stripe>
        <el-table-column label="#" type="index" width="50" />
        <el-table-column label="知乎问题" min-width="220" show-overflow-tooltip>
          <template #default="{ row }">
            <span class="preview-name">{{ row.question_short }}</span>
          </template>
        </el-table-column>
        <el-table-column label="回答方向" min-width="200">
          <template #default="{ row }">
            <el-tag size="small" type="warning">{{ row.direction_label }}</el-tag>
            <span class="preview-name">{{ row.direction_preview }}</span>
          </template>
        </el-table-column>
        <el-table-column label="平台" width="88">
          <template #default>
            <el-tag size="small" type="info">知乎</el-tag>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 生成结果 -->
    <el-card
      v-if="genJob.articles.length || genJob.errors.length || genJob.status === 'running'"
      class="preview-card"
    >
      <template #header>
        <div class="card-header">
          <span>生成结果</span>
          <el-tag v-if="genJob.status === 'running'" type="warning">进行中 {{ genJob.done }}/{{ genJob.total }}</el-tag>
          <el-tag v-else-if="genJob.status === 'completed'" type="success">已完成</el-tag>
        </div>
      </template>
      <el-table v-if="genJob.articles.length" :data="genJob.articles" size="small" stripe>
        <el-table-column prop="article_id" label="ID" width="72" />
        <el-table-column prop="direction_label" label="方向" width="140" />
        <el-table-column prop="title" label="标题" min-width="240" show-overflow-tooltip />
        <el-table-column label="操作" width="200">
          <template #default>
            <el-button link type="primary" @click="goTasks">任务队列</el-button>
            <el-button link type="primary" @click="goHistory">文章管理</el-button>
          </template>
        </el-table-column>
      </el-table>
      <ul v-if="genJob.errors.length" class="error-list">
        <li v-for="(e, i) in genJob.errors" :key="i">{{ e.direction }}：{{ e.error }}</li>
      </ul>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onActivated } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Plus, Refresh } from '@element-plus/icons-vue'
import { zhihuQaApi } from '../api'

let _keySeq = 0
function nextKey() {
  _keySeq += 1
  return `k_${_keySeq}`
}

export default {
  name: 'ZhihuQa',
  setup() {
    const router = useRouter()
    const dirTableRef = ref(null)
    const form = reactive({
      question: '',
      extraHint: '',
      runSearch: true,
      articlePromptKey: '',
    })
    const materials = ref([])
    const searchMeta = reactive({ count: null, queries: [] })
    const directions = ref([])
    const loadingDirections = ref(false)
    const generating = ref(false)

    // 写正文 System 变体
    const articlePromptOptions = ref([
      { key: '', name: '默认（写正文 System）', is_default: true, source: 'builtin' },
    ])
    const loadingArticlePrompts = ref(false)

    const loadArticlePrompts = async () => {
      loadingArticlePrompts.value = true
      try {
        const res = await zhihuQaApi.getArticlePrompts()
        const items = (res?.items || []).filter(Boolean)
        if (items.length) {
          articlePromptOptions.value = items
          // 当前选中的 key 已不在新列表里 → 回落到默认
          const exists = items.some(it => (it.key || '') === (form.articlePromptKey || ''))
          if (!exists) form.articlePromptKey = ''
        }
      } catch (e) {
        ElMessage.warning('加载写正文模板列表失败：' + (e?.message || ''))
      } finally {
        loadingArticlePrompts.value = false
      }
    }
    const genJob = reactive({
      taskId: null,
      status: '',
      total: 0,
      done: 0,
      articles: [],
      errors: [],
    })
    let pollTimer = null

    const selectedCount = computed(() =>
      directions.value.filter(d => d.selected && (d.text || '').trim()).length
    )

    const allDirectionsSelected = computed(() => {
      const list = directions.value
      if (!list.length) return false
      return list.every(d => d.selected)
    })

    const directionsIndeterminate = computed(() => {
      const list = directions.value
      if (!list.length) return false
      const n = list.filter(d => d.selected).length
      return n > 0 && n < list.length
    })

    const previewJobs = computed(() => {
      const q = form.question.trim()
      if (!q) return []
      return directions.value
        .filter(d => d.selected && (d.text || '').trim())
        .map(d => ({
          question_short: q.length > 48 ? q.slice(0, 48) + '…' : q,
          direction_label: d.label || '未命名',
          direction_preview:
            (d.text || '').length > 40 ? (d.text || '').slice(0, 40) + '…' : d.text,
        }))
    })

    const genProgress = computed(() => {
      if (!genJob.total) return 0
      return Math.round((genJob.done / genJob.total) * 100)
    })

    const toggleSelectAllDirections = (checked) => {
      directions.value.forEach(d => {
        d.selected = !!checked
      })
    }

    const fetchDirections = async () => {
      if (!form.question.trim()) return
      loadingDirections.value = true
      try {
        const res = await zhihuQaApi.generateDirections({
          question: form.question.trim(),
          extra_hint: form.extraHint.trim(),
          run_search: form.runSearch,
        })
        if (form.runSearch) {
          materials.value = res.materials || []
          searchMeta.count = res.count ?? materials.value.length
          searchMeta.queries = res.queries || []
        } else {
          materials.value = []
          searchMeta.count = null
          searchMeta.queries = []
        }
        const list = (res.directions || []).map(d => ({
          _key: nextKey(),
          id: d.id,
          label: d.label || '',
          text: d.text || '',
          selected: true,
        }))
        directions.value = list
        if (!list.length) {
          ElMessage.warning('未解析到回答方向，请重试或点击「添加自定义方向」')
        } else {
          ElMessage.success(`已生成 ${list.length} 条方向，请勾选后生成正文`)
        }
      } catch (e) {
        const msg = e?.message || ''
        const hint = msg.includes('timeout') ? '（联网检索较慢，已放宽超时，请重试）' : ''
        ElMessage.error('拆方向失败：' + msg + hint)
      } finally {
        loadingDirections.value = false
      }
    }

    const addCustomDirection = () => {
      if (!form.question.trim()) {
        ElMessage.warning('请先填写知乎问题')
        return
      }
      directions.value.push({
        _key: nextKey(),
        id: `custom_${Date.now()}`,
        label: '自定义方向',
        text: '',
        selected: true,
      })
    }

    const removeDirection = (idx) => {
      directions.value.splice(idx, 1)
    }

    const selectAll = () => {
      toggleSelectAllDirections(true)
    }

    const selectNone = () => {
      toggleSelectAllDirections(false)
    }

    const resetForm = () => {
      form.question = ''
      form.extraHint = ''
      form.runSearch = true
      form.articlePromptKey = ''
      materials.value = []
      searchMeta.count = null
      searchMeta.queries = []
      directions.value = []
      genJob.taskId = null
      genJob.status = ''
      genJob.total = 0
      genJob.done = 0
      genJob.articles = []
      genJob.errors = []
      if (pollTimer) clearTimeout(pollTimer)
    }

    const pollStatus = () => {
      if (pollTimer) clearTimeout(pollTimer)
      const tick = async () => {
        try {
          const s = await zhihuQaApi.getGenerateStatus(genJob.taskId)
          genJob.status = s.status
          genJob.total = s.total ?? s.total_count ?? 0
          genJob.done = s.done ?? ((s.completed_count || 0) + (s.failed_count || 0))
          genJob.articles = s.articles || []
          genJob.errors = s.errors || []
          if (['completed', 'cancelled', 'failed'].includes(s.status)) {
            generating.value = false
            const ok = s.completed_count ?? genJob.articles.length
            ElMessage.success(`生成结束：成功 ${ok} 篇，可在任务队列查看详情`)
            return
          }
          pollTimer = setTimeout(tick, 2000)
        } catch (e) {
          generating.value = false
          ElMessage.error('查询进度失败')
        }
      }
      tick()
    }

    const startGenerate = async () => {
      const selected = directions.value.filter(
        d => d.selected && (d.text || '').trim()
      )
      if (!selected.length) {
        const checked = directions.value.filter(d => d.selected).length
        if (checked > 0) {
          ElMessage.warning('已勾选的方向需填写「回答核心方向」说明（不能为空）')
        } else {
          ElMessage.warning('请至少勾选一条方向，并填写回答核心方向')
        }
        return
      }
      generating.value = true
      genJob.articles = []
      genJob.errors = []
      genJob.status = 'running'
      try {
        const res = await zhihuQaApi.startGenerate({
          question: form.question.trim(),
          extra_hint: form.extraHint.trim(),
          materials: materials.value,
          article_prompt_key: form.articlePromptKey || '',
          directions: selected.map(d => ({
            id: d.id,
            label: d.label,
            text: d.text.trim(),
          })),
        })
        genJob.taskId = res.task_id ?? Number(res.job_id)
        genJob.status = 'pending'
        genJob.total = res.total
        genJob.done = 0
        ElMessage.info(`任务 #${genJob.taskId} 已加入任务队列`)
        pollStatus()
      } catch (e) {
        generating.value = false
        genJob.status = ''
        ElMessage.error('启动生成失败：' + (e.message || ''))
      }
    }

    const goHistory = () => {
      router.push('/content/library')
    }

    const goTasks = () => {
      router.push('/content/tasks')
    }

    onMounted(() => {
      loadArticlePrompts()
    })
    // 用户从「提示词模板」编辑回来时刷新列表
    onActivated(() => {
      loadArticlePrompts()
    })

    return {
      dirTableRef,
      form,
      searchMeta,
      directions,
      loadingDirections,
      generating,
      genJob,
      selectedCount,
      previewJobs,
      genProgress,
      articlePromptOptions,
      loadingArticlePrompts,
      loadArticlePrompts,
      fetchDirections,
      addCustomDirection,
      removeDirection,
      selectAll,
      selectNone,
      resetForm,
      allDirectionsSelected,
      directionsIndeterminate,
      toggleSelectAllDirections,
      startGenerate,
      goHistory,
      goTasks,
      Plus,
      Refresh,
    }
  },
}
</script>

<style scoped>
.zhihu-qa {
  padding: 0;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.preview-note {
  margin: 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.45;
  max-width: 720px;
}

.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.hint-text {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}

.count-hint {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
}

.search-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.query-chip {
  font-size: 12px;
  color: #606266;
  background: #f5f7fa;
  padding: 2px 8px;
  border-radius: 4px;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.directions-card {
  margin-top: 20px;
}

.preview-card {
  margin-top: 20px;
}

.preview-name {
  margin-left: 6px;
  font-size: 12px;
  color: #606266;
}

.error-list {
  color: #f56c6c;
  font-size: 13px;
  margin: 12px 0 0;
  padding-left: 18px;
}
</style>
